/* eslint-disable react/no-unstable-nested-components */
import { useQuery } from "@apollo/client";
import type { CellContext } from "@tanstack/react-table";
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import useRootIssuer from "components/company/v3/context/hooks/useRootIssuer";
import UnlockPremiumContentCTA from "components/conversion/UnlockPremiumContentCTA";
import FinancialsSheetQuery from "components/financials/modern/graphql/FinancialsSheetQuery";
import type {
  IFinancialsSheetQuery,
  IFinancialsSheetQueryVariables,
} from "components/financials/modern/graphql/__generated__/FinancialsSheetQuery.generated";
import getPlatformVerticalByIssuer from "components/financials/modern/utils/platformVertical";
import SadFaceIcon from "components/icons/SadFaceIcon";
import LoadingSkeleton from "components/pages/TemplatesGalleryPage/LoadingSkeleton";
import { ScrollArea } from "components/ui/ScrollArea";
import classNames from "components/ui/classNames";
import { parseISO } from "date-fns";
import getYear from "date-fns/getYear";
import type { IAmericanCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/AmericanCompanyIssuerTopLevel.generated";
import type { IBrazilianCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/BrazilianCompanyIssuerTopLevel.generated";
import type { IBrazilianRealEstateFundTopLevelFragment } from "lib/fragments/__generated__/BrazilianRealEstateFundTopLevel.generated";
import useSelfie from "lib/hooks/useSelfie";
import { useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  IFinancialsSheetIndicatorsCriterion,
  IFinancialsSheetLimitCriterion,
  IFinancialsStatementType,
  IIndicatorDataSource,
  IIndicatorsTablePlacement,
  ISecurityCountry,
} from "types/graphqlTypes";

const INDICATOR_IDS = {
  EXECUTIVE_OFFICERS: "ind_01hsy2wmpyfet88v09mekba46h", // Diretoria - Remuneração Total
  BOARD_OF_DIRECTORS: "ind_01hsyjm32pf6w9qywrcna5ec98", // Conselho de Administração - Remuneração Total
  FISCAL_COUNCIL: "ind_01hsym5zkkeq2vd5zyp83xh2fa", // Conselho Fiscal - Remuneração Total
  TOTAL_COMPENSATION: "ind_01hsynfpj2f1na95neprr6q1fc", // Executivos + Conselho de Administração + Conselho Fiscal

  // Ações
  BR_NET_INCOME: "ind_01hqv3nmtyfsn9w9ke0q8n73b1", // Lucro Líquido
  BR_NET_REVENUE: "ind_01hqv29wf5e4y99kqv21v30e1a", // Receita Líquida

  // Stocks/REITs indicators
  US_NET_INCOME: "ind_01h64d63j4e2ardgpvdt5dws7b", // Net Income
  US_TOTAL_REVENUE: "ind_01h64d6axaf4kvbvwh1n5cs5qz", // Total Revenue
  US_OPERATING_CASH_FLOW: "ind_01h64d74qde9sv3z12rvxsf8q1", // Operating Cash Flow (for REITs)
} as const;

interface CompensationRow {
  fiscalYear: string;

  formattedBoardOfDirectors: string;
  formattedBoardOfExecutiveOfficers: string;
  formattedFiscalCouncil: string;
  formattedTotalCompensation: string;
  totalCompensation: number;

  formattedNetIncome: string;
  formattedRevenue: string;
  formattedOperatingCashFlow: string;

  netIncome: number;
  revenue: number;
  operatingCashFlow: number;
}

function getIndicatorsToFetch(securityCountry: ISecurityCountry) {
  if (securityCountry === ISecurityCountry.Br) {
    return [
      INDICATOR_IDS.EXECUTIVE_OFFICERS,
      INDICATOR_IDS.BOARD_OF_DIRECTORS,
      INDICATOR_IDS.FISCAL_COUNCIL,
      INDICATOR_IDS.TOTAL_COMPENSATION,

      INDICATOR_IDS.BR_NET_INCOME,
      INDICATOR_IDS.BR_NET_REVENUE,
    ];
  }

  return [
    INDICATOR_IDS.EXECUTIVE_OFFICERS,
    INDICATOR_IDS.BOARD_OF_DIRECTORS,
    INDICATOR_IDS.FISCAL_COUNCIL,
    INDICATOR_IDS.TOTAL_COMPENSATION,

    INDICATOR_IDS.US_NET_INCOME,
    INDICATOR_IDS.US_TOTAL_REVENUE,
    INDICATOR_IDS.US_OPERATING_CASH_FLOW,
  ];
}

export default function TotalCompensationsContent() {
  const issuer = useRootIssuer();
  if (issuer.__typename === "BrazilianCompanyIssuer") {
    if (issuer.securities.length === 0) {
      return null;
    }
    return <TotalCompensationsContentTable tickerSymbol={issuer.securities[0].tickerSymbol} issuer={issuer} />;
  }
  if (issuer.__typename === "AmericanCompanyIssuer") {
    return <TotalCompensationsContentTable tickerSymbol={issuer.primaryTickerSymbol} issuer={issuer} />;
  }
  throw new Error("Compensations content is not supported for this issuer");
}

interface TotalCompensationsContentTableProps {
  tickerSymbol: string;
  issuer:
    | IAmericanCompanyIssuerTopLevelFragment
    | IBrazilianCompanyIssuerTopLevelFragment
    | IBrazilianRealEstateFundTopLevelFragment;
}
function TotalCompensationsContentTable({ tickerSymbol, issuer }: TotalCompensationsContentTableProps) {
  const intl = useIntl();
  const viewer = useSelfie();

  // Determine issuer characteristics
  const isBrazilian = issuer.__typename === "BrazilianCompanyIssuer";
  const isAmerican = issuer.__typename === "AmericanCompanyIssuer";
  const isREIT = issuer.__typename === "AmericanCompanyIssuer" && issuer.isREIT;

  const { data, loading } = useQuery<IFinancialsSheetQuery, IFinancialsSheetQueryVariables>(FinancialsSheetQuery, {
    variables: {
      vertical: getPlatformVerticalByIssuer(issuer),
      tickerSymbol,
      country: isBrazilian ? ISecurityCountry.Br : ISecurityCountry.Us,
      statementType: IFinancialsStatementType.Annual,
      indicators: getIndicatorsToFetch(isAmerican ? ISecurityCountry.Us : ISecurityCountry.Br),
      indicatorsCriterion: IFinancialsSheetIndicatorsCriterion.Only,
      limit: 30,
      limitCriterion: IFinancialsSheetLimitCriterion.NumberOfRows,
      indicatorDataSource: isBrazilian ? IIndicatorDataSource.Cvm : IIndicatorDataSource.Morningstar,
      currency: isBrazilian ? "BRL" : "USD",
      indicatorsPlacement: IIndicatorsTablePlacement.Start,
      financialsTemplate: null,
      filterByReportDateAfter: null,
      filterByReportDateBefore: null,
      filterByInterim: null,
      filterByCountryCode: null,
    },
  });

  const tableData = useMemo<CompensationRow[]>(() => {
    if (!data?.financialsSheet?.indicators) {
      return [];
    }

    const { indicators: sheetIndicators, reportDates } = data.financialsSheet;
    const indicatorMap = new Map(sheetIndicators.map((indicator) => [indicator.objectId, indicator]));

    // Build rows for each fiscal year
    return reportDates
      .map((reportDate, index) => {
        const isTTM = data.financialsSheet.isTrailingTwelveMonths[index];
        if (isTTM) {
          return null;
        }

        const getValue = (indicatorId: string): number => {
          const indicator = indicatorMap.get(indicatorId);
          if (!indicator?.series?.[index]) {
            return 0;
          }

          const node = indicator.series[index];
          if (
            node.__typename === "FinancialIndicatorSeriesFinancialNode" ||
            node.__typename === "FinancialIndicatorSeriesCurrencyNode" ||
            node.__typename === "FinancialIndicatorSeriesMetricNode" ||
            node.__typename === "FinancialIndicatorSeriesNumberNode" ||
            node.__typename === "FinancialIndicatorSeriesPercentNode"
          ) {
            return node.value;
          }
          return 0;
        };
        const getFormattedValue = (indicatorId: string): string => {
          const indicator = indicatorMap.get(indicatorId);
          return indicator?.series?.[index]?.formattedValue ?? "0";
        };

        const fiscalYear = String(getYear(parseISO(reportDate)));
        const formattedBoardOfExecutiveOfficers = getFormattedValue(INDICATOR_IDS.EXECUTIVE_OFFICERS);
        const formattedBoardOfDirectors = getFormattedValue(INDICATOR_IDS.BOARD_OF_DIRECTORS);
        const formattedFiscalCouncil = getFormattedValue(INDICATOR_IDS.FISCAL_COUNCIL);
        const formattedTotalCompensation = getFormattedValue(INDICATOR_IDS.TOTAL_COMPENSATION);
        const totalCompensation = getValue(INDICATOR_IDS.TOTAL_COMPENSATION);

        if (totalCompensation === 0) {
          return null;
        }

        if (isBrazilian) {
          const netIncome = getValue(INDICATOR_IDS.BR_NET_INCOME);
          const revenue = getValue(INDICATOR_IDS.BR_NET_REVENUE);

          return {
            fiscalYear,
            formattedBoardOfExecutiveOfficers,
            formattedBoardOfDirectors,
            formattedFiscalCouncil,
            formattedTotalCompensation,
            totalCompensation,

            formattedNetIncome: getFormattedValue(INDICATOR_IDS.BR_NET_INCOME),
            formattedRevenue: getFormattedValue(INDICATOR_IDS.BR_NET_REVENUE),
            netIncome,
            revenue,
            operatingCashFlow: 0,
            formattedOperatingCashFlow: "",
          };
        }

        const netIncome = getValue(INDICATOR_IDS.US_NET_INCOME);
        const revenue = getValue(INDICATOR_IDS.US_TOTAL_REVENUE);
        const operatingCashFlow = isREIT ? getValue(INDICATOR_IDS.US_OPERATING_CASH_FLOW) : 0;

        return {
          fiscalYear,
          formattedBoardOfExecutiveOfficers,
          formattedBoardOfDirectors,
          formattedFiscalCouncil,
          formattedTotalCompensation,
          totalCompensation,

          formattedNetIncome: getFormattedValue(INDICATOR_IDS.US_NET_INCOME),
          formattedRevenue: getFormattedValue(INDICATOR_IDS.US_TOTAL_REVENUE),
          netIncome,
          revenue,
          formattedOperatingCashFlow: getFormattedValue(INDICATOR_IDS.US_OPERATING_CASH_FLOW),
          operatingCashFlow,
        };
      })
      .filter((row): row is CompensationRow => row !== null && row !== undefined)
      .sort((a, b) => {
        const yearA = parseInt(a.fiscalYear, 10);
        const yearB = parseInt(b.fiscalYear, 10);
        return yearA - yearB;
      });
  }, [data, isBrazilian, isREIT]);

  const tooltipMonetaryScaleMessage = isBrazilian
    ? intl.formatMessage({
        defaultMessage: "Escala: Em reais",
        id: "vSWhDO",
      })
    : intl.formatMessage({
        defaultMessage: "Escala: Em dólar",
        id: "PRjDDA",
      });
  const columnHelper = createColumnHelper<CompensationRow>();

  const columns = useMemo(() => {
    const cols = [
      columnHelper.accessor((row) => row.fiscalYear, {
        header: intl.formatMessage({
          defaultMessage: "Ano",
          id: "ptn1Js",
          description: "Remuneração dos Administradores",
        }),
        meta: {
          header: {
            className: classNames(
              "text-white dark:text-slate-100",
              "border-r border-slate-500 dark:border-slate-600",
              "bg-slate-600 dark:bg-slate-700",
            ),
          },
          cell: {
            className: classNames(
              "font-mono text-sm font-medium",
              "border-l-0 border-r border-t border-gray-500 dark:border-gray-600",
              "text-center text-white",
              "bg-slate-600 dark:bg-slate-700 dark:text-slate-100",
            ),
          },
        },
      }),

      // Executive Officers / Diretoria
      columnHelper.accessor((row) => row.formattedBoardOfExecutiveOfficers, {
        header: intl.formatMessage({
          defaultMessage: "Diretoria",
          id: "kGjrtV",
          description: "Remuneração dos Administradores",
        }),
        cell: (props: CellContext<CompensationRow, string>) => props.getValue() ?? "",
        meta: {
          header: {
            tooltip: tooltipMonetaryScaleMessage,
            className: classNames(
              "border-r border-slate-200 dark:border-slate-700",
              "bg-slate-100 dark:bg-slate-800",
              "text-slate-600 dark:text-slate-300",
            ),
          },
          cell: {
            className: classNames(
              "font-mono text-sm font-medium",
              "border border-b-0 border-l-0",
              "text-slate-600 dark:text-slate-300",
              "border-slate-200 text-center dark:border-slate-700",
            ),
          },
        },
      }),

      // Board of Directors
      columnHelper.accessor((row) => row.formattedBoardOfDirectors, {
        header: intl.formatMessage({
          defaultMessage: "Conselho de Administração",
          id: "+syG2y",
          description: "Remuneração dos Administradores",
        }),
        cell: (props: CellContext<CompensationRow, string>) => props.getValue() ?? "",
        meta: {
          header: {
            tooltip: tooltipMonetaryScaleMessage,
            className: classNames(
              "border-r border-slate-200 dark:border-slate-700",
              "bg-slate-100 dark:bg-slate-800",
              "text-slate-600 dark:text-slate-300",
            ),
          },
          cell: {
            className: classNames(
              "font-mono text-sm font-medium",
              "border border-b-0 border-l-0",
              "text-slate-600 dark:text-slate-300",
              "border-slate-200 text-center dark:border-slate-700",
            ),
          },
        },
      }),
    ];

    // Brazilian-only: Fiscal Council
    if (isBrazilian) {
      cols.push(
        columnHelper.accessor((row) => row.formattedFiscalCouncil, {
          header: intl.formatMessage({
            defaultMessage: "Conselho Fiscal",
            id: "5atf4D",
            description: "Remuneração dos Administradores",
          }),
          cell: (props: CellContext<CompensationRow, string>) => props.getValue() ?? "",
          meta: {
            header: {
              tooltip: tooltipMonetaryScaleMessage,
              className: classNames(
                "border-r border-slate-200 dark:border-slate-700",
                "bg-slate-100 dark:bg-slate-800",
                "text-slate-600 dark:text-slate-300",
              ),
            },
            cell: {
              className: classNames(
                "font-mono text-sm font-medium",
                "border border-b-0 border-l-0",
                "text-slate-600 dark:text-slate-300",
                "border-slate-200 text-center dark:border-slate-700",
              ),
            },
          },
        }),
      );
    }

    // Total Compensation
    cols.push(
      columnHelper.accessor((row) => row.formattedTotalCompensation, {
        header: intl.formatMessage({
          defaultMessage: "Total",
          id: "KKoQ7p",
          description: "Remuneração dos Administradores",
        }),
        cell: (props: CellContext<CompensationRow, string>) => props.getValue() ?? "",
        meta: {
          header: {
            tooltip: tooltipMonetaryScaleMessage,
            className: "bg-sky-100 text-blue-600 dark:bg-sky-900/30 dark:text-blue-400",
          },
          cell: {
            className: classNames(
              "font-mono text-sm font-medium",
              "border border-b-0 border-l-0",
              "text-blue-600 dark:text-blue-300",
              "border-slate-200 text-center dark:border-slate-700",
            ),
          },
        },
      }),
    );

    if (!isREIT) {
      cols.push(
        columnHelper.accessor((row) => row.formattedNetIncome, {
          header: intl.formatMessage({
            defaultMessage: "Lucro Líquido",
            id: "ZWn5dZ",
            description: "Remuneração dos Administradores",
          }),
          cell: (props: CellContext<CompensationRow, string>) => props.getValue() ?? "",
          meta: {
            header: {
              tooltip: tooltipMonetaryScaleMessage,
              className: classNames(
                "border-l border-green-200 dark:border-green-800",
                "bg-[#F1FFF3] dark:bg-green-950/20",
                "text-[#2f8132] dark:text-green-400",
                "tracking-wide",
              ),
            },
            cell: {
              className: (props: CellContext<CompensationRow, string>) => {
                const { netIncome } = props.row.original;
                return classNames(
                  "border border-b-0 border-l-0 border-slate-200 dark:border-slate-700",
                  "text-center font-mono text-sm font-medium",
                  {
                    "text-[#3ea383] dark:text-green-400": netIncome >= 0,
                    "text-[#c74343] dark:text-red-400": netIncome < 0,
                  },
                );
              },
            },
          },
        }),
        columnHelper.accessor((row) => formatPercentageOfNetIncome(row), {
          header: intl.formatMessage({
            defaultMessage: "% Lucro",
            id: "HiAJXj",
            description: "Remuneração dos Administradores",
          }),
          meta: {
            header: {
              className: classNames(
                "border-l tracking-wide",
                "bg-[#faf4e5] dark:bg-yellow-950/20",
                "text-[#997d2e] dark:text-yellow-500",
                "border-b border-slate-200 dark:border-slate-700",
              ),
            },
            cell: {
              className: (props: CellContext<CompensationRow, string>) => {
                return getClassNameForPercentageOfNetIncome(props.row.original);
              },
            },
          },
        }),
      );
    } else {
      cols.push(
        columnHelper.accessor((row) => row.formattedOperatingCashFlow, {
          header: "OCF",
          cell: (props: CellContext<CompensationRow, string>) => props.getValue() ?? "",
          meta: {
            header: {
              tooltip: tooltipMonetaryScaleMessage,
              className: classNames(
                "border-l border-green-200 dark:border-green-800",
                "bg-[#F1FFF3] dark:bg-green-950/20",
                "text-[#2f8132] dark:text-green-400",
                "tracking-wide",
              ),
            },
            cell: {
              className: (props: CellContext<CompensationRow, number | undefined>) => {
                const { operatingCashFlow } = props.row.original;
                return classNames(
                  "border border-b-0 border-l-0 border-slate-200 dark:border-slate-700",
                  "text-center font-mono text-sm font-medium",
                  {
                    "text-[#2f8132] dark:text-green-400": operatingCashFlow > 0,
                    "text-red-500 dark:text-red-400": operatingCashFlow < 0,
                  },
                );
              },
            },
          },
        }),
        columnHelper.accessor((row) => formatPercentageOfOperatingCashFlow(row), {
          header: "% OCF",
          meta: {
            header: {
              className: classNames(
                "border-l tracking-wide",
                "bg-[#faf4e5] dark:bg-yellow-950/20",
                "text-[#997d2e] dark:text-yellow-500",
                "border-b border-slate-200 dark:border-slate-700",
              ),
            },
            cell: {
              className: (props: CellContext<CompensationRow, string>) => {
                return getClassNameForPercentageOfOperatingCashFlow(props.row.original);
              },
            },
          },
        }),
      );
    }

    // Revenue
    cols.push(
      columnHelper.accessor((row) => row.formattedRevenue, {
        header: intl.formatMessage({
          defaultMessage: "Receita",
          id: "bid7Xd",
          description: "Remuneração dos Administradores",
        }),
        cell: (props: CellContext<CompensationRow, string>) => props.getValue() ?? "",
        meta: {
          header: {
            tooltip: isBrazilian
              ? intl.formatMessage({
                  defaultMessage: "Escala: Em milhões",
                  id: "UFUJaz",
                })
              : intl.formatMessage({
                  defaultMessage: "Escala: Em milhões",
                  id: "UFUJaz",
                }),
            className: classNames(
              "border-l tracking-wide",
              "bg-[#F1FFF3] dark:bg-green-950/20",
              "text-[#2f8132] dark:text-green-400",
              "border-b border-slate-200 dark:border-slate-700",
            ),
          },
          cell: {
            className: classNames(
              "font-mono text-sm font-medium",
              "border border-b-0",
              "text-[#57ae5b] dark:text-green-400",
              "border-slate-200 text-center dark:border-slate-700",
            ),
          },
        },
      }),
    );
    cols.push(
      columnHelper.accessor((row) => formatPercentageOfRevenue(row), {
        header: "% Receita",
        meta: {
          header: {
            className: classNames(
              "border-l tracking-wide",
              "bg-[#faf4e5] dark:bg-yellow-950/20",
              "text-[#997d2e] dark:text-yellow-500",
              "border-b border-slate-200 dark:border-slate-700",
            ),
          },
          cell: {
            className: (props: CellContext<CompensationRow, string>) => {
              return getClassNameForPercentageOfRevenue(props.row.original);
            },
          },
        },
      }),
    );

    return cols;
  }, [columnHelper, intl, isBrazilian, isREIT]);

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (loading) {
    return <LoadingSkeleton count={1} className="min-h-96 xl:h-full" />;
  }

  if (!data || tableData.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="space-y-2 text-center text-base tracking-wide text-gray-600">
          <div>
            <SadFaceIcon />
          </div>

          <p>
            <FormattedMessage defaultMessage="Nenhum dado foi encontrado." id="i1Elih" />
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full space-y-4 font-aeonik font-normal">
      <ScrollArea type="auto" orientation="horizontal" className="relative h-full w-full">
        <div className="pb-4 lg:w-full lg:pb-0">
          <div
            className={classNames("relative overflow-auto rounded-md border border-slate-200 dark:border-slate-950")}
          >
            <table className="w-full divide-y divide-slate-200 dark:divide-slate-700">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr
                    key={headerGroup.id}
                    className="select-none divide-x divide-slate-200 dark:divide-slate-700 [&>th]:whitespace-nowrap"
                  >
                    {headerGroup.headers.map((header) => {
                      return (
                        <th
                          key={header.id}
                          className={classNames(
                            "px-3 py-1 text-sm font-semibold",
                            header.column.columnDef.meta?.header?.className,
                          )}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
              <tbody
                className={classNames(
                  "[&>tr>td]:text-center",
                  "[&>tr>td:not(:first-of-type)]:border-x",
                  "[&>tr>td:not(:first-of-type)]:border-[#dbe2eb] dark:[&>tr>td:not(:first-of-type)]:border-slate-700",
                  "[&>tr:hover]:bg-slate-50 dark:[&>tr:hover]:bg-slate-800/50",
                )}
              >
                {table.getRowModel().rows.map((row) => {
                  return (
                    <tr key={row.id} className="group divide-x divide-gray-200 dark:divide-slate-700">
                      {row.getVisibleCells().map((cell) => {
                        const cellClassNames =
                          typeof cell.column.columnDef.meta?.cell?.className === "function"
                            ? cell.column.columnDef.meta?.cell?.className(cell)
                            : cell.column.columnDef.meta?.cell?.className;

                        return (
                          <td
                            key={cell.id}
                            className={classNames("px-3 py-1.5 font-mono text-sm font-semibold", cellClassNames)}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </ScrollArea>

      {!viewer?.isPremium && <UnlockPremiumContentCTA origin="Compensations" />}

      {isAmerican && (
        <div className="w-full rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700 dark:border-amber-800 dark:bg-amber-950/20 dark:text-amber-400">
          Os dados podem estar desatualizados devido à troca de fornecedor, que será concluída nas próximas semanas.
        </div>
      )}
    </div>
  );
}

function formatPercentageOfNetIncome({ netIncome, totalCompensation }: CompensationRow) {
  const value = netIncome === 0 ? 0 : totalCompensation / 1_000_000 / netIncome;
  if (netIncome < 0) {
    return "P";
  }
  return `${value >= 10 ? Math.round(value * 100) : (value * 100).toFixed(2).replace(".", ",")}%`;
}

function formatPercentageOfOperatingCashFlow({ operatingCashFlow, totalCompensation }: CompensationRow) {
  const value = operatingCashFlow === 0 ? 0 : totalCompensation / 1_000_000 / operatingCashFlow;
  return `${value >= 10 ? Math.round(value) : value.toFixed(2).replace(".", ",")}%`;
}

function formatPercentageOfRevenue({ revenue, totalCompensation }: CompensationRow) {
  const value = revenue === 0 ? 0 : totalCompensation / 1_000_000 / revenue;
  return `${value >= 10 ? Math.round(value * 100) : (value * 100).toFixed(2).replace(".", ",")}%`;
}

function getClassNameForPercentageOfNetIncome({ netIncome, totalCompensation }: CompensationRow) {
  const value = (netIncome === 0 ? 0 : totalCompensation / 1_000_000 / netIncome) * 100;
  if (value < 0) {
    return classNames(
      "border-t border-slate-200 bg-[#FCF1F1] text-center font-mono text-sm font-medium text-[#c74343]",
      "dark:border-slate-700 dark:bg-red-950/20 dark:text-red-400",
    );
  }

  if (value >= 10) {
    return classNames(
      "border-t border-slate-200 bg-[#FCF1F1] text-center font-mono text-sm font-medium text-[#c74343]",
      "dark:border-slate-700 dark:bg-red-950/20 dark:text-red-400",
    );
  }

  if (value < 10 && value > 3) {
    return classNames(
      "border-t border-slate-200 bg-[#fffbea] text-center font-mono text-sm font-medium text-[#7e6626]",
      "dark:border-slate-700 dark:bg-yellow-950/20 dark:text-yellow-500",
    );
  }

  if (value <= 3) {
    return classNames(
      "border-t border-slate-200 bg-[#F1FFF3] text-center font-mono text-sm font-medium text-[#2f8132]",
      "dark:border-slate-700 dark:bg-green-950/20 dark:text-green-400",
    );
  }

  return classNames("border-t border-slate-200 text-center font-mono text-sm font-medium", "dark:border-slate-700");
}

function getClassNameForPercentageOfOperatingCashFlow({ operatingCashFlow, totalCompensation }: CompensationRow) {
  const value = (operatingCashFlow === 0 ? 0 : totalCompensation / 1_000_000 / operatingCashFlow) * 100;
  if (value < 0) {
    return classNames(
      "border-t border-slate-200 bg-[#FCF1F1] text-center font-mono text-sm font-medium text-[#c74343]",
      "dark:border-slate-700 dark:bg-red-950/20 dark:text-red-400",
    );
  }

  if (value >= 10) {
    return classNames(
      "border-t border-slate-200 bg-[#FCF1F1] text-center font-mono text-sm font-medium text-[#c74343]",
      "dark:border-slate-700 dark:bg-red-950/20 dark:text-red-400",
    );
  }

  if (value < 10 && value > 3) {
    return classNames(
      "border-t border-slate-200 bg-[#fffbea] text-center font-mono text-sm font-medium text-[#7e6626]",
      "dark:border-slate-700 dark:bg-yellow-950/20 dark:text-yellow-500",
    );
  }

  if (value <= 3) {
    return classNames(
      "border-t border-slate-200 bg-[#F1FFF3] text-center font-mono text-sm font-medium text-[#2f8132]",
      "dark:border-slate-700 dark:bg-green-950/20 dark:text-green-400",
    );
  }

  return classNames("border-t border-slate-200 text-center font-mono text-sm font-medium", "dark:border-slate-700");
}

function getClassNameForPercentageOfRevenue({ revenue, totalCompensation }: CompensationRow) {
  const value = (revenue === 0 ? 0 : totalCompensation / 1_000_000 / revenue) * 100;

  if (value >= 2) {
    return classNames(
      "border-t border-slate-200 bg-[#FCF1F1] text-center font-mono text-sm font-medium text-[#c74343]",
      "dark:border-slate-700 dark:bg-red-950/20 dark:text-red-400",
    );
  }

  if (value < 2 && value > 0.5) {
    return classNames(
      "border-t border-slate-200 bg-[#fffbea] text-center font-mono text-sm font-medium text-[#7e6626]",
      "dark:border-slate-700 dark:bg-yellow-950/20 dark:text-yellow-500",
    );
  }

  if (value <= 0.5) {
    return classNames(
      "border-t border-slate-200 bg-[#F1FFF3] text-center font-mono text-sm font-medium text-[#2f8132]",
      "dark:border-slate-700 dark:bg-green-950/20 dark:text-green-400",
    );
  }

  return classNames("border-t border-slate-200 text-center font-mono text-sm font-medium", "dark:border-slate-700");
}
